import { ref } from "vue";
import { toTitleCase } from "@/src/utils/utils.js";
import { telephonyProviders } from "@/src/constants/telephonyConstants.js";
import {
  callStatuses,
} from "@/src/constants/callConstants.js";
import {
  getCampaignCallSid,
  shouldOpenPostCallForm,
} from "@/src/utils/callsUtils.js";
import store from "@/src/store";
import router from "@/src/router/index.js";
import eventBus from "@/src/utils/eventBus.js";
import plivoEventHandler from "./plivoEventHandler.js";
import sdkLoader from "@/src/services/telephony/sdkLoader.js";
import providerManager from "@/src/services/telephony/providerManager.js";
import BaseTelephonyDevice from "@/src/services/telephony/base/baseTelephonyDevice.js";
import campaignCallHandler from "@/src/services/telephony/campaignCallHandler.js";

export default class PlivoDevice extends BaseTelephonyDevice {
  constructor(provider = telephonyProviders.plivo) {
    super(provider);
    this.plivoWebSdk = null;
    this.isReady = ref(false);
    this.currentConnection = ref(null);
    this.incomingConnection = ref(null);
    this.credentials = null;
    this.callStatus = ref("idle");
    this.actualCallUUID = null;
    this.campaignCallSid = null;
    this.callEnded = false;
    this.eventListenersAttached = false;
    this.boundEventHandlers = {};
    this.lastRefreshTime = Date.now();
  }

  setupWebkitSpeechRecognitionPolyfill() {
    if (
      typeof window !== "undefined" &&
      !window.webkitSpeechRecognition &&
      !window.SpeechRecognition
    ) {
      console.warn(
        "SpeechRecognition not supported, creating mock implementation",
      );

      class MockSpeechRecognition {
        constructor() {
          this.continuous = false;
          this.interimResults = false;
          this.lang = "en-US";
          this.maxAlternatives = 1;
          this.serviceURI = "";
          this.grammars = null;

          this.onstart = null;
          this.onend = null;
          this.onerror = null;
          this.onresult = null;
          this.onnomatch = null;
          this.onspeechstart = null;
          this.onspeechend = null;
          this.onsoundstart = null;
          this.onsoundend = null;
          this.onaudiostart = null;
          this.onaudioend = null;
        }

        start() {
          console.warn(
            "Mock SpeechRecognition: start() called - no actual recognition performed",
          );
          setTimeout(() => {
            if (this.onstart) this.onstart();
            setTimeout(() => {
              if (this.onend) this.onend();
            }, 100);
          }, 50);
        }

        stop() {
          console.warn("Mock SpeechRecognition: stop() called");
          if (this.onend) this.onend();
        }

        abort() {
          console.warn("Mock SpeechRecognition: abort() called");
          if (this.onend) this.onend();
        }

        addEventListener(event, callback) {
          this[`on${event}`] = callback;
        }

        removeEventListener(event, callback) {
          this[`on${event}`] = null;
        }
      }

      window.webkitSpeechRecognition = MockSpeechRecognition;
      window.SpeechRecognition = MockSpeechRecognition;
    }
  }

  async initialize(credentials) {
    try {
      if (!credentials || typeof credentials !== "object") {
        throw new Error(
          "Valid credentials object is required for Plivo device initialization",
        );
      }

      this.credentials = credentials;
      this.setupWebkitSpeechRecognitionPolyfill();

      sdkLoader.storePlivoCredentials(this.provider, credentials);

      this.plivoWebSdk = await sdkLoader.initializePlivoSDK(this.provider);

      await sdkLoader.switchPlivoProvider(this.provider);
      
      this.setupEventListeners();
      this.eventListenersAttached = true;
      this.isReady.value = true;

      try {
        await plivoEventHandler.initialize(this.plivoWebSdk);
      } catch (error) {
        console.error(`[PlivoDevice] Failed to initialize Plivo event handler for ${this.provider}:`, error);
      }
    } catch (error) {
      console.error(`[PlivoDevice] ${this.provider} initialization failed:`, error);
      throw error;
    }
  }


  async _handleAutoAnswerError(callUUID, error) {
    try {
      console.error(`${this.provider}: Failed to auto-answer campaign call:`, error);
      
      await campaignCallHandler.handleAutoAnswerError(this.provider, error);
      
      if (callUUID && this.plivoWebSdk?.client) {
        try {
          this.plivoWebSdk.client.reject(callUUID);
        } catch (rejectError) {
          console.error(`${this.provider}: Error rejecting call:`, rejectError);
        }
      }
      
      this.resetCallState();
    } catch (err) {
      console.error(`${this.provider}: Error handling auto answer error:`, err);
    }
  }

  async _autoAnswerMPCCall(callUUID) {
    try {
      if (!callUUID) {
        throw new Error("Call UUID is required");
      }

      const permissionResult = await campaignCallHandler.requestMicrophonePermission();
      
      if (!permissionResult.success) {
        throw permissionResult.error;
      }
      
      if (!this.plivoWebSdk?.client) {
        throw new Error("Plivo SDK not available");
      }
      
      this.plivoWebSdk.client.answer(callUUID);
      
      this.callStatus.value = callStatuses.OPEN;
      
      await campaignCallHandler.updateStoreOnCampaignAnswer();
    } catch (error) {
      await this._handleAutoAnswerError(callUUID, error);
    }
  }

  async _handleMPCIncomingCall(callUUID, srcNumber, destEndpoint, mpcCampaignId) {
    try {
      if (!campaignCallHandler.validateCampaignCallParams(callUUID, srcNumber, destEndpoint, mpcCampaignId)) {
        console.error(`${this.provider}: Invalid parameters for campaign incoming call`);
        return;
      }

      this.callStatus.value = callStatuses.CONNECTING;
      this.callEnded = false;
      this.actualCallUUID = callUUID;
      
      this.currentConnection.value = this.createConnectionObject(
        callUUID,
        srcNumber,
        destEndpoint
      );
      
      const callDetails = campaignCallHandler.createCampaignCallDetails(
        srcNumber,
        destEndpoint,
        mpcCampaignId
      );
      
      callDetails.provider = this.provider;
      
      const storeUpdateSuccess = await campaignCallHandler.updateStoreForCampaignCall(
        this.provider,
        mpcCampaignId,
        callDetails
      );
      
      if (!storeUpdateSuccess) {
        console.error(`${this.provider}: Failed to update store for campaign call`);
        this.resetCallState();
        return;
      }
      
      await this._autoAnswerMPCCall(callUUID);
    } catch (error) {
      console.error(`${this.provider}: Error handling campaign incoming call:`, error);
      this.resetCallState();
    }
  }

  updateCallUUID(callInfo) {
    try {
      if (!callInfo?.callUUID) {
        return;
      }
      
      const currentCallSid = this.currentConnection.value?.parameters?.CallSid;
      
      if (!currentCallSid || !currentCallSid.startsWith("plivo_call")) {
        return;
      }
      
      this.actualCallUUID = callInfo.callUUID;
      if (this.currentConnection.value) {
        this.currentConnection.value.parameters.CallSid = callInfo.callUUID;
      }
    } catch (error) {
      console.error(`${this.provider}: Error updating call UUID:`, error);
    }
  }

  _handleNormalIncomingCall(callUUID, srcNumber, destEndpoint, actualToNumber, entityId, entityType) {
    try {
      if (!callUUID) {
        console.error(`${this.provider}: Invalid call UUID for normal incoming call`);
        return;
      }

      const phoneNumber = srcNumber?.replace(/[@phone.plivo.com]/g, "") || "";
      const toNumber = actualToNumber || destEndpoint?.replace(/[@phone.plivo.com]/g, "") || "";
      this.callStatus.value = callStatuses.PENDING;
      this.callEnded = false;
      this.actualCallUUID = callUUID;
      
      this.incomingConnection.value = this.createConnectionObject(
        callUUID,
        srcNumber,
        actualToNumber || destEndpoint
      );
      
      store.commit("telephony/setProvider", this.provider);
      
      const callerDetails = {
        name: phoneNumber || "Unknown Caller",
        number: phoneNumber,
        From: phoneNumber,
        To: toNumber,
        status: callStatuses.PENDING,
        provider: this.provider,
        endpoint: destEndpoint,
        entityId: entityId,
        entityType: entityType,
      };
      
      store.dispatch("calls/startIncomingCall", callerDetails);
    } catch (error) {
      console.error(`${this.provider}: Error handling normal incoming call:`, error);
      this.resetCallState();
    }
  }

  setupEventListeners() {
    if (!this.plivoWebSdk) return;

    this.plivoWebSdk.client.on("onLogin", () => {
      console.info(
        `${this.provider}: onLogin - Successfully logged in as ${this.credentials?.username}`,
      );
      this.isReady.value = true;

      if (this.credentials?.username) {
        providerManager.setEndpointMapping(
          this.credentials.username,
          this.provider,
        );
      }

      store.dispatch("telephony/setProviderDeviceReady", {
        provider: this.provider,
        deviceReady: true,
      });
    });

    this.plivoWebSdk.client.on("onLoginFailed", (cause) => {
      console.info(`${this.provider}: onLoginFailed - ${cause}`);
      this.isReady.value = false;
    });

    this.plivoWebSdk.client.on("onCalling", (callInfo) => {
      console.info(`${this.provider}: onCalling`, callInfo);
      if (!this.isCurrentProvider()) return;

      this.callStatus.value = callStatuses.CONNECTING;
      this.callEnded = false;
      this.updateCallUUID(callInfo);
    });

    this.plivoWebSdk.client.on("onCallRemoteRinging", (callInfo) => {
      console.info(`${this.provider}: onCallRemoteRinging`, callInfo);
      if (!this.isCurrentProvider()) return;

      this.updateCallUUID(callInfo);
      this.callStatus.value = callStatuses.RINGING;
      store.dispatch("calls/updateCallStatus", callStatuses.RINGING);
    });

    this.plivoWebSdk.client.on("onCallConnected", (callInfo) => {
      console.info(`${this.provider}: onCallConnected`, callInfo);
    });

    this.plivoWebSdk.client.on("onMediaConnected", (callInfo) => {
      console.info(`${this.provider}: onMediaConnected`, callInfo);
      this.updateCallUUID(callInfo);
    });

    this.plivoWebSdk.client.on("onCallAnswered", (callInfo) => {
      console.info(`${this.provider}: onCallAnswered`, callInfo);
      if (!this.isCurrentProvider()) return;

      this.callStatus.value = callStatuses.OPEN;
      store.dispatch("calls/updateCallStatus", callStatuses.OPEN);

      this.updateCallUUID(callInfo);

      if (callInfo?.callUUID) {
        this.campaignCallSid = callInfo.callUUID;
        if (
          this.currentConnection.value?._callbacks &&
          this.currentConnection.value._callbacks.accept
        ) {
          this.currentConnection.value._callbacks.accept();
        }
      }

      if (callInfo?.direction === "incoming") {
        this.currentConnection.value = this.createConnectionObject(
          callInfo.callUUID,
          callInfo.src,
          callInfo.dest,
        );
        this.incomingConnection.value = null;
      }
    });

    this.plivoWebSdk.client.on("onCallTerminated", (evt, callInfo) => {
      console.info(`${this.provider}: onCallTerminated - ${evt}`, callInfo);
      if (!this.isCurrentProvider()) return;

      if (!this.callEnded) {
        this.callStatus.value = callStatuses.CLOSED;
        this.handleCallEnd();
      }
    });

    this.plivoWebSdk.client.on("onCallFailed", (reason, callInfo) => {
      console.info(`${this.provider}: onCallFailed - ${reason}`, callInfo);
      if (!this.isCurrentProvider()) return;

      if (!this.callEnded) {
        this.callStatus.value = callStatuses.CLOSED;
        this.handleCallEnd();
      }
    });

    this.plivoWebSdk.client.on(
      "onIncomingCall",
      async (callerID, extraHeaders, callInfo, callerName) => {
        console.info(
          `${this.provider}: onIncomingCall:`,
          callerID,
          extraHeaders,
          callInfo,
          callerName,
        );

        if (callInfo) {
          console.info(
            `${this.provider}: Incoming call details - UUID: ${callInfo.callUUID}, From: ${callInfo.src}, To: ${callInfo.dest}`,
          );
        }

        const callUUID = callInfo?.callUUID || callInfo;
        const srcNumber = callInfo?.src || callerID;
        const destEndpoint = callInfo?.dest || this.credentials?.username;

        const mappedProvider = providerManager.getProviderForEndpoint(destEndpoint);
        if (mappedProvider && mappedProvider !== this.provider) {
          console.log(
            `${this.provider}: Call is for ${mappedProvider}, ignoring`,
          );
          return;
        }

        if (!mappedProvider && destEndpoint !== this.credentials?.username) {
          console.log(
            `${this.provider}: Call not for this provider's endpoint, ignoring`,
          );
          return;
        }

        const existingIncomingCall = store.getters["calls/incomingCallActive"];
        if (existingIncomingCall) {
          return;
        }
        
        const campaignId = campaignCallHandler.extractCampaignId(extraHeaders);
        const isCampaignCall = campaignCallHandler.isCampaignCall(campaignId);
  
        if (isCampaignCall) {
          await this._handleMPCIncomingCall(callUUID, srcNumber, destEndpoint, campaignId);
          return;
        }
  
        const parsedHeaders = campaignCallHandler.parseExtraHeaders(extraHeaders);
        const actualToNumber = parsedHeaders.to || "";
        const entityId = parsedHeaders.entityId;
        const entityType = parsedHeaders.entityType;
        this._handleNormalIncomingCall(callUUID, srcNumber, destEndpoint, actualToNumber, entityId, entityType);
      },
    );

    this.plivoWebSdk.client.on("onIncomingCallCanceled", (callInfo) => {
      console.info(`${this.provider}: onIncomingCallCanceled`, callInfo);
      if (!this.isCurrentProvider()) return;

      if (callInfo) {
        console.info(
          `${this.provider}: Incoming call canceled - UUID: ${callInfo.callUUID}`,
        );
      }
      if (!this.callEnded) {
        this.callStatus.value = callStatuses.CLOSED;
        this.incomingConnection.value = null;
        this.actualCallUUID = null;
        store.dispatch("calls/endIncomingCall");
        this.resetCallState();
      }
    });

    this.plivoWebSdk.client.on("onIncomingCallIgnored", (callInfo) => {
      console.info(`${this.provider}: onIncomingCallIgnored`, callInfo);
      if (!this.isCurrentProvider()) return;

      if (callInfo) {
        console.info(
          `${this.provider}: Incoming call ignored - UUID: ${callInfo.callUUID}`,
        );
      }
      if (!this.callEnded) {
        this.callStatus.value = callStatuses.CLOSED;
        this.incomingConnection.value = null;
        this.actualCallUUID = null;
        store.dispatch("calls/endIncomingCall");
        this.resetCallState();
      }
    });

    this.plivoWebSdk.client.on("onConnectionChange", (connectionInfo) => {
      console.log(`${this.provider}: Connection change:`, connectionInfo);
      if (connectionInfo.state === "connected") {
        this.isReady.value = true;
      }
    });
  }

  createConnectionObject(callUUID, from, to) {
    const connectionObj = {
      parameters: {
        CallSid: callUUID,
        From: from,
        To: to,
      },
      status: () => {
        return this.callStatus.value;
      },
      disconnect: () => {
        this.plivoWebSdk.client.hangup();
      },
      on: (event, callback) => {
        if (!connectionObj._callbacks) {
          connectionObj._callbacks = {};
        }
        connectionObj._callbacks[event] = callback;

        if (event === "accept" && this.callStatus.value === "open") {
          setTimeout(() => {
            callback();
          }, 0);
        }
      },
      sendDigits: (digits) => this.plivoWebSdk.client.sendDtmf(digits),
      mute: (shouldMute) =>
        shouldMute
          ? this.plivoWebSdk.client.mute()
          : this.plivoWebSdk.client.unmute(),
    };

    return connectionObj;
  }

  handleCallEnd() {
    if (this.callEnded) {
      return;
    }
    const callSid = this.currentConnection.value?.parameters?.CallSid;
    const isCampaign = store.getters["calls/isCampaign"];
    
    this.callEnded = true;
    if (callSid) {
      if (isCampaign) {
        const shouldOpenForm = shouldOpenPostCallForm();
        const campaignCallSid = getCampaignCallSid(
          this.campaignCallSid,
          plivoEventHandler.getCampaignCallSid(),
        );
        
        if (campaignCallSid) {
          setTimeout(() => {
            eventBus.emit("call-log-added", true);
          }, 1200);

          const campaignId = store.getters["callsCampaign/currentCampaignId"];

          router.push({
            name: "sales-dialer-campaign-detail",
            params: { id: campaignId },
          });

          if (shouldOpenForm) {
            store.dispatch("callsCampaign/setDrawerVisible", true).then();
            eventBus.emit("call-ended", campaignCallSid);
          }
        }
        eventBus.emit("campaign-next-step", true);
        setTimeout(() => {
          store.dispatch("callsCampaign/refreshCampaignDetails").then();
        }, 2000);
      } else {
          eventBus.emit("call-ended", callSid);
      }
    }

    if (isCampaign) {
      this.campaignCallSid = null;
      plivoEventHandler.clearCampaignCallSid();
      store.dispatch("calls/setIsCampaign", false);
      store.dispatch("callsCampaign/updateCallOngoing", false);
    }
    store.dispatch("calls/updateCallStatus", callStatuses.CLOSED);

    if (this.currentConnection.value) {
      this.currentConnection.value = null;
      store.dispatch("calls/endOutgoingCall");
    }

    if (this.incomingConnection.value) {
      this.incomingConnection.value = null;
      store.dispatch("calls/endIncomingCall");
    }

    setTimeout(() => {
      eventBus.emit("call-hangup", true);
      this.resetCallState();
    }, 600);
  }

  resetCallState() {
    this.callStatus.value = callStatuses.IDLE; 
    this.actualCallUUID = null;
    this.callEnded = false;
    this.currentConnection.value = null;
    this.incomingConnection.value = null;
  }

  ensureEventListenersActive() {
    if (!this.eventListenersAttached && this.plivoWebSdk) {
      this.setupEventListeners();
      this.eventListenersAttached = true;
    }
  }

  isCurrentProvider() {
    return sdkLoader.getCurrentPlivoProvider() === this.provider;
  }

  async login() {
    return Promise.resolve();
  }

  async makeCall(params) {
    this._validateFeature("callOutbound");
    try {
      await sdkLoader.switchPlivoProvider(this.provider);

      this.ensureEventListenersActive();

      if (!this.plivoWebSdk || !sdkLoader.isPlivoLoggedIn(this.provider)) {
        console.error(`[PlivoDevice] ${this.provider} SDK not ready!`);
        throw new Error("Plivo SDK not ready");
      }

      this.resetCallState();
      await this.delay(100);

      const extraHeaders = {
        "X-PH-CampaignId": params.CampaignId?.toString() || "0",
        "X-PH-EntityId": params.Id?.toString() || "",
        "X-PH-EntityType": params.entityType || "contact",
        "X-PH-TaskId": params.taskId?.toString() || "0",
        "X-PH-From": params.From,
        "X-PH-UserId": localStorage.getItem("userId")?.toString() || "",
        "X-PH-callerId": params.callerId || "",
      };

      this.callStatus.value = callStatuses.CONNECTING;
      this.currentConnection.value = this.createConnectionObject(
        `plivo_call_${Date.now()}`,
        params.From,
        params.To,
      );

      const callResult = this.plivoWebSdk.client.call(params.To, extraHeaders);

      if (!callResult) {
        this.resetCallState();
        console.error(`[PlivoDevice] ${this.provider} failed to initiate call`);
        throw new Error("Failed to initiate call");
      }

      return this.currentConnection.value;
    } catch (error) {
      console.error(`[PlivoDevice] ${this.provider} makeCall failed:`, error);
      if (error.name === "NotFoundError" || error.name === "NotReadableError") {
        console.error("Audio device error detected");
      }
      this.resetCallState();
      throw error;
    }
  }

  async hangupCall() {
    if (this.plivoWebSdk && !this.callEnded) {
      this.handleCallEnd();
      this.plivoWebSdk.client.hangup();
      await this.delay(100);
    }
  }

  async answerIncomingCall() {
    if (!this.incomingConnection.value) {
      console.error("No incoming call to answer");
      throw new Error("No incoming call to answer");
    }

    console.log(`[${this.provider}] Answering incoming call`);

    try {
      const storedConnection = this.incomingConnection.value;
      const callUUID =
        storedConnection?.parameters?.CallSid || this.actualCallUUID;

      if (
        !sdkLoader.isPlivoLoggedIn(this.provider) ||
        sdkLoader.getCurrentPlivoProvider() !== this.provider
      ) {
        await sdkLoader.switchPlivoProvider(this.provider);
        this.ensureEventListenersActive();
        this.incomingConnection.value = storedConnection;
      }

      if (!this.plivoWebSdk || !sdkLoader.isPlivoLoggedIn(this.provider)) {
        console.error(`[PlivoDevice] ${this.provider} SDK not ready after provider switch!`);
        throw new Error("Plivo SDK not ready after provider switch");
      }

      if (this.currentConnection.value) {
        await this.hangupCall();
        await this.delay(100);
      }

      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (permissionError) {
        console.error("Audio permission error:", permissionError);
        if (
          permissionError.name === "NotFoundError" ||
          permissionError.name === "NotReadableError"
        ) {
          console.error(
            "Microphone not available. Please check your audio device settings.",
          );
        }
        await this.rejectIncomingCall();
        throw permissionError;
      }

      console.log(`Answering call with UUID: ${callUUID}`);
      const result = this.plivoWebSdk.client.answer(callUUID);

      this.currentConnection.value = storedConnection;
      this.incomingConnection.value = null;
      this.callStatus.value = callStatuses.OPEN;

      await store.dispatch("calls/updateCallStatus", callStatuses.OPEN);
      await store.dispatch("calls/setIsCallConnected", true);
      store.commit("telephony/setProvider", this.provider);

      return this.currentConnection.value;
    } catch (error) {
      console.error("Error answering call:", error);
      await this.rejectIncomingCall();
      return null;
    }
  }

  async rejectIncomingCall() {
    if (this.plivoWebSdk && this.incomingConnection.value) {
      try {
        const callUUID = this.incomingConnection.value.parameters.CallSid;
        this.plivoWebSdk.client.reject(callUUID);
        this.incomingConnection.value = null;
        this.resetCallState();
        await store.dispatch("calls/endIncomingCall");
      } catch (error) {
        console.error("Error rejecting call:", error);
      }
    }
  }

  async toColdTransfer(params) {
    throw new Error("Cold transfer not yet implemented for Plivo");
  }

  async toWarmTransfer(params) {
    throw new Error("Warm transfer not yet implemented for Plivo");
  }

  async swapCalls() {
    throw new Error("Call swap not yet implemented for Plivo");
  }

  async startRecording() {
    throw new Error("Call recording not yet implemented for Plivo");
  }

  async stopRecording() {
    throw new Error("Call recording not yet implemented for Plivo");
  }

  async getVoicemails() {
    throw new Error("Voicemails not yet implemented for Plivo");
  }

  async purchasePhoneNumber(params) {
    throw new Error("Phone number purchase not yet implemented for Plivo");
  }

  async deletePhoneNumber(params) {
    throw new Error("Phone number deletion not yet implemented for Plivo");
  }

  getDeviceStatus() {
    return {
      isReady: this.isReady.value,
      hasActiveCall: !!this.currentConnection.value && !this.callEnded,
      hasIncomingCall: !!this.incomingConnection.value && !this.callEnded,
    };
  }

  getCurrentConnection() {
    return this.callEnded ? null : this.currentConnection.value;
  }

  getIncomingConnection() {
    return this.callEnded ? null : this.incomingConnection.value;
  }

  async refresh() {
    console.log(`Refreshing ${this.provider} device...`);
    try {
      if (this.credentials) {
        await this.initialize(this.credentials);
        this.lastRefreshTime = Date.now();
        console.log(`${this.provider} device refreshed successfully`);
      } else {
        console.warn(`${this.provider}: No stored credentials for refresh`);
      }
    } catch (error) {
      console.error(`Failed to refresh ${this.provider} device:`, error);
      throw error;
    }
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  destroy() {
    try {
      if (this.plivoWebSdk) {
        this.plivoWebSdk.client.logout();
      }
      this.resetCallState();
      this.eventListenersAttached = false;
    } catch (error) {
      console.error(`Error destroying ${this.provider} device:`, error);
    }
  }
}

